/**
 * Centralized API Service
 *
 * Provides a singleton API client instance. Auth is cookie-based (httpOnly
 * PHX-1180 cookie, sent via withCredentials) — no Authorization header is
 * attached. Also handles response/error handling and 401 refresh-token retry.
 *
 * All micro-services are reached through the gateway at VITE_API_URL.
 * Individual API files use the service prefixes from API_ROUTES
 * (e.g. API_ROUTES.PRODUCTS = /products/api/v1).
 */
import type { AxiosInstance } from 'axios';
import { createApiClient } from '@tms-phoenix/ui-vue';
import { config } from '@/core/config';

// ---------------------------------------------------------------------------
// Shared gateway client  (same as soho app)
// ---------------------------------------------------------------------------

let apiInstance: AxiosInstance | null = null;

/**
 * Get the singleton API client instance.
 * Uses createApiClient from @tms-phoenix/ui-vue which:
 *   - relies solely on the httpOnly PHX-1180 auth cookie (withCredentials) — no Authorization header is ever sent
 *   - unwraps ApiResponse<T> envelope so response.data = inner payload
 *   - captures pagination metadata (flat or nested) from meta onto response.pagination
 *   - handles 401 refresh-token with mutex and retry via onRefreshToken callback
 */
export function getApiClient(): AxiosInstance {
  if (apiInstance) return apiInstance;

  apiInstance = createApiClient({
    baseURL: config.api.baseUrl,
    timeout: config.api.timeout,
    // ── 401 token-refresh callback ───────────────────────────────────────
    // Called by createApiClient when any authenticated request returns 401.
    // Lazy-imports auth store to break the circular dependency
    // (auth.store → api.service → auth.store).
    onRefreshToken: async () => {
      const { useAuthStore } = await import('../../stores/auth.store');
      const authStore = useAuthStore();
      const success = await authStore.refreshAccessToken();
      return success ? authStore.accessToken : null;
    },
    // A refreshed token that still 401s means the session is invalid server-side —
    // force a client-side logout instead of retrying refresh again.
    onAuthFailure: async () => {
      const { useAuthStore } = await import('../../stores/auth.store');
      useAuthStore().forceLogout();
    },
    // SV-563: a 401 from /auth/session-check is handled by authStore.validateSession() itself.
    skipRefreshForUrls: ['/auth/login', '/auth/logout', '/auth/refresh', '/auth/session-check'],
  });

  // ── Tenant hostname header ────────────────────────────────────────────────
  // Send x-tenant-hostname on every request so backend services can
  // resolve the tenant context (Cognito pool, DB schema, etc.).
  apiInstance.interceptors.request.use((requestConfig) => {
    if (requestConfig.headers) {
      requestConfig.headers['x-tenant-hostname'] = window.location.hostname;
    }
    return requestConfig;
  });

  return apiInstance;
}

/** Reset shared client (useful for testing or after auth changes). */
export function resetApiClient(): void {
  apiInstance = null;
}
